/**
 * Helpers for subscribing the socket client to the customer:<id> rooms of the
 * customers in a user's list, so that back-end customer-updated events for ANY
 * of those customers reach this client (previously only the active customer
 * context was subscribed). See VF-2565.
 *
 * Joining a room the socket is already in is a no-op on the server, so
 * subscribeToCustomerRooms is safe to call on every user/context change and on
 * (re)connect.
 */

/**
 * Emits a `join` for the customer:<id> room of every customer the given user
 * can see.
 *
 * Root/admin users (view_rootentity) can see EVERY customer in the system; we
 * deliberately do not subscribe them per-customer (it would mean thousands of
 * rooms and a refetch on every customer edit anywhere). They keep the existing
 * cache-expiry refresh.
 *
 * @param {Object} socket the vue-socket.io-extended $socket instance (or null)
 * @param {Object} user a UserRecord (has .can() and .customers)
 * @return {undefined}
 */
export const subscribeToCustomerRooms = (socket, user) => {
  if (!socket || user.can('view_rootentity')) {
    return
  }

  const customers = user.customers || []
  customers.forEach((customer) => {
    socket.client.emit('join', `customer:${customer.id}`)
  })
}

/**
 * Emits a `leave` for the customer:<id> room of every customer that was in the
 * previous list but is not in the new one, so we stop receiving updates for
 * customers the user can no longer see.
 *
 * @param {Object} socket the vue-socket.io-extended $socket instance (or null)
 * @param {Array} previousCustomers the customer list before the update
 * @param {Object} user the UserRecord after the update
 * @return {undefined}
 */
export const unsubscribeFromRemovedCustomerRooms = (socket, previousCustomers, user) => {
  if (!socket) {
    return
  }

  const currentIds = (user.customers || []).map((customer) => customer.id)
  previousCustomers
    .filter((customer) => !currentIds.includes(customer.id))
    .forEach((customer) => {
      socket.client.emit('leave', `customer:${customer.id}`)
    })
}
